import { doc, getDoc, setDoc, updateDoc, deleteDoc, arrayUnion, query, where, collection, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";

// ---- USER DOCS ----

function createUserDoc(uid, data) {
  return setDoc(doc(db, "users", uid), {
    email: data.email,
    displayName: data.displayName,
    createdAt: new Date().toISOString(),
    memberships: data.memberships || []
  });
}

function getUserDoc(uid) {
  return getDoc(doc(db, "users", uid)).then(function (snap) {
    return snap.exists() ? snap.data() : null;
  });
}

function updateUserDoc(uid, data) {
  return updateDoc(doc(db, "users", uid), data);
}

function addMembershipToUser(uid, membership) {
  return updateDoc(doc(db, "users", uid), {
    memberships: arrayUnion(membership)
  });
}

// ---- COMPANY DOCS ----

function createCompanyDoc(companyId, data) {
  return setDoc(doc(db, "companies", companyId), {
    name: data.name,
    createdAt: new Date().toISOString(),
    createdBy: data.createdBy
  });
}

// providerId lets security rules scope a provider's writes to their own records;
// inviteCode lets the rules verify an invite-based join against the invite document.
// Admin permission switches. A member doc with no `permissions` is a full
// admin; with a map, each switch must be explicitly true.
var PERMISSION_KEYS = [
  { key: "publishSchedules", label: "Publish schedules", desc: "Move a month from Review to Published and edit live schedules" },
  { key: "approveRequests", label: "Approve requests", desc: "Approve or deny time-off, swap and preference requests" },
  { key: "manageProviders", label: "Manage providers and hospitals", desc: "Add and edit providers, hospitals, shifts and credentials" },
  { key: "managePay", label: "Manage pay", desc: "Pay rates, bonuses, timecards and payroll" },
  { key: "manageTeam", label: "Manage team", desc: "Invite people and change their roles and permissions" },
  { key: "manageSettings", label: "Manage settings", desc: "Company settings, notification credentials and AI configuration" }
];
function fullPermissions() {
  var p = {}; PERMISSION_KEYS.forEach(function (k) { p[k.key] = true; }); return p;
}
// True when `member` (a members/{uid} doc) may do `key`. Providers never can;
// admins without a permissions map can do everything.
function memberCan(member, key) {
  if (!member || member.role !== "admin") return false;
  if (!member.permissions) return true;
  return member.permissions[key] === true;
}

function addCompanyMember(companyId, uid, role, providerId, inviteCode, permissions, email) {
  return setDoc(doc(db, "companies", companyId, "members", uid), {
    role: role || "provider",
    providerId: providerId === undefined ? null : providerId,
    inviteCode: inviteCode || null,
    permissions: permissions || null,
    email: email || null,
    joinedAt: new Date().toISOString()
  });
}

// One-off read of the caller's member doc (null when it does not exist).
function getOwnMember(companyId, uid) {
  return getDoc(doc(db, "companies", companyId, "members", uid)).then(function (snap) { return snap.exists() ? snap.data() : null; });
}

function listenOwnMember(companyId, uid, callback) {
  return onSnapshot(doc(db, "companies", companyId, "members", uid), function (snap) {
    callback(snap.exists() ? snap.data() : null);
  }, function () { callback(null); });
}

function listenCompanyMembers(companyId, callback) {
  return onSnapshot(collection(db, "companies", companyId, "members"), function (snap) {
    var out = []; snap.forEach(function (d) { out.push({ uid: d.id, ...d.data() }); }); callback(out);
  }, function () { callback([]); });
}

function updateMemberPermissions(companyId, uid, permissions) {
  return updateDoc(doc(db, "companies", companyId, "members", uid), { permissions: permissions });
}

// Change a member's role. Promoting a provider keeps their provider link so
// they still get their own portal; demoting an admin requires a linked
// provider record (the portal is scoped to it).
function updateMemberRole(companyId, uid, role, providerId, permissions) {
  return updateDoc(doc(db, "companies", companyId, "members", uid), {
    role: role,
    providerId: providerId === undefined ? null : providerId,
    permissions: role === "admin" ? (permissions || null) : null
  });
}

// Link (or unlink, with null) a member to a provider record.
function updateMemberProvider(companyId, uid, providerId) {
  return updateDoc(doc(db, "companies", companyId, "members", uid), { providerId: providerId === undefined ? null : providerId });
}

// Remove a login from the company. Their users/{uid} entry is cleaned up by
// the app the next time they open this company (rules keep it theirs alone).
function removeCompanyMember(companyId, uid) {
  return deleteDoc(doc(db, "companies", companyId, "members", uid));
}

// Replace the caller's own cached memberships list in users/{uid}.
function setUserMemberships(uid, memberships) {
  return updateDoc(doc(db, "users", uid), { memberships: memberships });
}

// Create the caller's member doc only if it is missing. Never overwrites an
// existing one (which would wipe providerId), and never changes a role.
function ensureCompanyMember(companyId, uid, role, providerId, email) {
  var ref = doc(db, "companies", companyId, "members", uid);
  return getDoc(ref).then(function (snap) {
    if (snap.exists()) return true;
    return setDoc(ref, {
      role: role || "provider",
      providerId: providerId === undefined ? null : providerId,
      inviteCode: null,
      permissions: null,
      email: email || null,
      joinedAt: new Date().toISOString()
    }).then(function () { return true; });
  });
}

function updateCompanyDoc(companyId, data) {
  return updateDoc(doc(db, "companies", companyId), data);
}

function getCompanyDoc(companyId) {
  return getDoc(doc(db, "companies", companyId)).then(function (snap) {
    return snap.exists() ? snap.data() : null;
  });
}

// ---- INVITE DOCS ----

function getCompanyInvites(companyId) {
  var q = query(collection(db, "invites"), where("companyId", "==", companyId));
  return getDocs(q).then(function (snap) {
    var invites = [];
    snap.forEach(function (d) { invites.push({ code: d.id, ...d.data() }); });
    invites.sort(function (a, b) { return (b.createdAt || "") > (a.createdAt || "") ? 1 : -1; });
    return invites;
  });
}

function createInviteDoc(inviteCode, data) {
  return setDoc(doc(db, "invites", inviteCode), {
    companyId: data.companyId,
    companyName: data.companyName,
    email: data.email || "",
    role: data.role,
    providerId: data.providerId || null,
    permissions: data.permissions || null,
    createdBy: data.createdBy,
    createdAt: new Date().toISOString(),
    used: false
  });
}

function getInviteDoc(inviteCode) {
  return getDoc(doc(db, "invites", inviteCode)).then(function (snap) {
    return snap.exists() ? snap.data() : null;
  });
}

function markInviteUsed(inviteCode) {
  return updateDoc(doc(db, "invites", inviteCode), { used: true });
}

function deleteInvite(inviteCode) {
  return deleteDoc(doc(db, "invites", inviteCode));
}

export {
  createUserDoc, getUserDoc, updateUserDoc, addMembershipToUser,
  createCompanyDoc, getCompanyDoc, updateCompanyDoc, addCompanyMember, ensureCompanyMember, getCompanyInvites,
  PERMISSION_KEYS, fullPermissions, memberCan, getOwnMember, listenOwnMember, listenCompanyMembers, updateMemberPermissions,
  updateMemberRole, updateMemberProvider, removeCompanyMember, setUserMemberships,
  createInviteDoc, getInviteDoc, markInviteUsed, deleteInvite
};
